import {getDateParams, getValidDeliveryDate} from './data.helper';
import {
  SORT_TYPES,
  URL as CONST_URL,
  QUERY_PROMO_TAG_ID,
  MATCH_ALL_QUERY,
  SEARCH_CORRECT_OFF,
  SEARCH_RELATED_OFF,
  RELATED_HEADINGS,
  RELATED_PARTIAL_HEADING_PREFIX,
  RELATED_STRATEGY,
} from '../constants/constants';

export const getDeliveryDate = (basket, fulfillmentDetails, settings) => {
  if (basket && basket.fulfillAtSlot && basket.fulfillAtSlot.day) {
    return getValidDeliveryDate(basket.fulfillAtSlot.day);
  } else if (fulfillmentDetails) {
    return getValidDeliveryDate(fulfillmentDetails.date);
  } else if (settings) {
    return getDateParams(basket, settings).deliveryDate;
  }
  return null;
};

export const getPriceMulti = (prices, hasMixOffer) => {
  if (hasMixOffer) {
    return null;
  }
  let finalPrice;
  prices.map(price => {
    if (
      !finalPrice ||
      (finalPrice && price.min && finalPrice.min < price.min)
    ) {
      finalPrice = price;
    }
  });
  return finalPrice;
};

export const getMixAndSaveUrl = (renderMixNSaveTag, promoTagId) => {
  return `/search?${new URLSearchParams({
    adTitle: renderMixNSaveTag,
    q: `${QUERY_PROMO_TAG_ID}%3A%28${promoTagId}%29`,
    advanced: true,
  }).toString()}`;
};

// Curated-list browse URL. `adTitle` is what titles the page and breadcrumb —
// a list URL without one still works but renders with no heading, so always
// pass it where you can.
// Built key by key rather than from an object literal: URLSearchParams
// stringifies undefined to the literal "undefined", which would end up
// rendered as the page title.
export const getCuratedListUrl = (listId, adTitle, brandOwner) => {
  const search = new URLSearchParams();
  if (adTitle) {
    search.set('adTitle', adTitle);
  }
  search.set('listId', listId);
  if (brandOwner) {
    search.set('brandOwner', brandOwner);
  }
  return `/search?${search.toString()}`;
};

// Search-results URL for a /pandg category tile or pill. `pandg=true` is what
// tells SearchPage to render the pandg campaign banner/pill strip instead of
// its normal layout — it rides through the search API unread (see
// search.api.js's explicit variable whitelist) and drops away the moment the
// visitor runs any other search, which is what un-does the pandg styling.
export const getPandgSearchUrl = category => {
  const search = new URLSearchParams({
    q: category.query,
    adTitle: category.adTitle,
    size: 40,
    sortType: SORT_TYPES.CATEGORY,
    page: 0,
    pandg: true,
  });
  return `/search?${search.toString()}`;
};

const HALAL_WORD_REGEX = /\bhalal\b/i;

// Shared by App.js's main search submit and Header.js's suggestion click, so
// the two entry points into search stay in sync. An exact "halal" query goes
// straight to the halal landing page instead of a search results page;
// anything with halal as its own word (e.g. "halal chicken") still searches
// normally but gets tagged so SearchPage shows the halal banner/pill strip
// (see isHalal in SearchPage.js). \b keeps this from firing on a word that
// merely contains "halal" (e.g. "halalfoods").
export const isExactHalalKeyword = keyword =>
  !!keyword && keyword.trim().toLowerCase() === 'halal';

export const isHalalWordSearch = keyword =>
  HALAL_WORD_REGEX.test(keyword || '');

// Adds halal=true to an existing `?a=b&c=d`-style query string, re-encoding
// it in the process (URLSearchParams percent-encodes any raw spaces, unlike
// the literal strings in searchSuggestions.constants.js).
export const withHalalParam = urlParams => {
  const params = new URLSearchParams(urlParams);
  params.set('halal', 'true');
  return `?${params.toString()}`;
};

const RX = {
  whatsappLink: /(?:https?:\/\/)?(?:wa\.me\/|api\.whatsapp\.com\/send\b)/i,
  whatsappTerm: /\b(?:wa|whatsapp)\b/i,
  phoneLike: /(?:^|[^\w])(?:\+?\d[\d\s\-()]{6,}\d)(?:[^\w]|$)/,
  spamKeyword: /\b(?:szbant\.com|biaya|borongan|plafon|interior|gudang|pintu|transformer|fortress)\b/i,
  manyEncodedBytes: /(?:%[0-9A-Fa-f]{2}){3,}/,
  repeatedSeparators: /[%+_\-]{3,}/,

  // Any non-ASCII character at all, including 【 】 and emojis
  nonAscii: /[^\x20-\x7E]/,

  // Allow only ASCII letters/numbers/space and selected punctuation
  invalidAsciiChars: /[^a-zA-Z0-9\s_\-.,&/()'*:]/,
};

const safeDecodePlus = str => {
  if (!str) return '';
  const replaced = str.includes('+') ? str.replace(/\+/g, ' ') : str;
  try {
    return decodeURIComponent(replaced);
  } catch {
    return replaced;
  }
};

const toUrl = (input, baseUrl = `https://${CONST_URL.JJS_PROD}`) => {
  if (typeof input !== 'string' || input.trim() === '') {
    return null;
  }

  try {
    return new URL(input);
  } catch {
    try {
      return new URL(input, baseUrl);
    } catch {
      return null;
    }
  }
};

export const isPossibleSpamUrl = urlInput => {
  const urlObj = toUrl(urlInput);
  if (!urlObj) return false;

  const queryValue = urlObj.searchParams.get('q') || '';
  const decodedQuery = safeDecodePlus(queryValue);
  const fullUrl = urlObj.href;

  if (RX.whatsappLink.test(fullUrl)) return true;
  if (RX.whatsappTerm.test(decodedQuery) && RX.phoneLike.test(decodedQuery))
    return true;
  if (RX.phoneLike.test(decodedQuery)) return true;
  if (RX.spamKeyword.test(decodedQuery) || RX.spamKeyword.test(fullUrl))
    return true;
  if (RX.manyEncodedBytes.test(fullUrl)) return true;
  if (
    RX.repeatedSeparators.test(queryValue) ||
    RX.repeatedSeparators.test(fullUrl)
  )
    return true;

  // Hard block: any non-ASCII char in q, including 【 】 or emoji
  if (decodedQuery && RX.nonAscii.test(decodedQuery)) return true;

  // Hard block: ASCII chars outside your allowed set
  return !!(decodedQuery && RX.invalidAsciiChars.test(decodedQuery));
};

/**
 * Whether a search is something the customer typed, as opposed to a browse, a banner landing page
 * or a curated list - the only searches the related-products strip and the "couldn't find any
 * products matching ..." sentence are for. Banners (`adTitle`,
 * `first`) and curated lists choose their products deliberately, and a category or `*` browse has
 * no words to be related to.
 */
export const isTypedSearch = params => {
  if (!params) return false;
  const q = typeof params.q === 'string' ? params.q.trim() : '';
  if (!q || q === MATCH_ALL_QUERY || q === 'undefined') return false;
  return !(
    params.listId ||
    params.first ||
    params.adTitle ||
    params.advanced === 'true' ||
    params.advanced === true
  );
};

// The customer chose "Search instead for <their words>" - see SEARCH_CORRECT_PARAM.
export const isSearchCorrectionOff = params =>
  !!params && String(params.correct) === SEARCH_CORRECT_OFF;

// The link asked for no related-products strip - see SEARCH_RELATED_PARAM.
export const isRelatedProductsOff = params =>
  !!params && String(params.related) === SEARCH_RELATED_OFF;

/**
 * Variables for GQL_RELATED_PRODUCTS from a finished page-0 search, or null when the search is not
 * one the strip is for. The search's own results seed "similar products"; the featured cards on the
 * page are only excluded, so the strip never repeats a card the customer can already see.
 */
export const buildRelatedProductsRequest = ({
  params,
  searchProducts,
  featuredItemIds,
}) => {
  if (!isTypedSearch(params) || isRelatedProductsOff(params) || !searchProducts)
    return null;
  const results = searchProducts.results || [];
  const page = searchProducts.page || {};
  return {
    branch: params.b,
    deliveryDate: params.deliveryDate,
    // A corrected search's results are for the corrected words, so they are what is related to.
    query: searchProducts.correctedQuery || params.q,
    itemIds: results.map(product => product.itemId).filter(Boolean),
    excludeItemIds: (featuredItemIds || []).filter(Boolean),
    resultCount: page.totalElements || 0,
    parentSearchId: searchProducts.searchId,
  };
};

/**
 * The heading for the related strip: the web's own short copy per strategy (RELATED_HEADINGS),
 * "Results for "turkey"" for a single word, otherwise graph-product-search's title.
 */
export const getRelatedHeading = related => {
  if (!related) return null;
  if (RELATED_HEADINGS[related.strategy])
    return RELATED_HEADINGS[related.strategy];
  if (related.strategy === RELATED_STRATEGY.PARTIAL && related.searchedFor) {
    return `${RELATED_PARTIAL_HEADING_PREFIX} \u201c${related.searchedFor}\u201d`;
  }
  return related.title || null;
};
